import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, Mic, MapPin, X, ShieldCheck, 
  Building2, ChevronRight, ChevronLeft, ArrowLeft,
  FlaskConical, ShoppingBag, HeartPulse, Sparkles,
  Clock, CheckCircle2, Star, ArrowUpRight, Award,
  Check, ArrowRight, Package, Tag, Zap, Flame, Trash2
} from 'lucide-react';
import axios from 'axios';
import { fetchLabsData, fetchNearbyLabsData } from '../../services/LabTestApi/LabList';
import { fetchSymptoms } from '../../services/LabTestApi/symptomsApi';
import { fetchAllPackagesFromAPI, calculatePackagePrice } from '../../services/HomePage/AllPackagesAPI';
import { getAllTests } from '../../services/LabTestApi/PopularTest';
import { useCartActions } from '../../hooks/useCartActions';
import { useLocationContext } from '../../context/LocationContext';
import PackageCard from './PackageCard';
import LabCard from './LabCard';
import TestCard from './TestCard';

export default function LabList() {
  const navigate = useNavigate();
  const location = useLocation();
  const { coords, address, selectedCity } = useLocationContext();

  const cart = useSelector((state) => state.cart.items || []);
  const { addWithReplaceCheck, removeItem, LoginPopupComponent } = useCartActions();

  const [labs, setLabs] = useState([]);
  const [symptoms, setSymptoms] = useState([]);
  const [packages, setPackages] = useState([]);
  const [tests, setTests] = useState([]);
  const [selectedLabId, setSelectedLabId] = useState('all');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const packageSliderRef = useRef(null);
  const testSliderRef = useRef(null);

  const slidePackages = (direction) => {
    if (packageSliderRef.current) {
      const scrollAmount = direction === 'left' ? -320 : 320;
      packageSliderRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  const slideTests = (direction) => {
    if (testSliderRef.current) {
      const scrollAmount = direction === 'left' ? -320 : 320;
      testSliderRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  const getDistanceKm = (lat1, lon1, lat2, lon2) => {
    if (lat1 == null || lon1 == null || lat2 == null || lon2 == null) return 999;
    const R = 6371;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a = Math.sin(dLat / 2) ** 2 +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon / 2) ** 2;
    return 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)) * R;
  };

  // Fetch Labs, Symptoms, Tests & Live Packages Location-Wise
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const userLat = selectedCity?.latitude ?? selectedCity?.lat ?? coords?.lat;
        const userLng = selectedCity?.longitude ?? selectedCity?.lng ?? coords?.lng;

        // 1. All Labs from API for reference and location-filtered labs
        let allLabs = [];
        try {
          const allLabsRes = await axios.get('https://admin.medrphalabs.com/api/Lab');
          if (Array.isArray(allLabsRes.data)) allLabs = allLabsRes.data;
          else if (Array.isArray(allLabsRes.data?.data)) allLabs = allLabsRes.data.data;
        } catch (e) {
          console.warn('Could not fetch all labs list:', e);
        }

        const labsData = await fetchLabsData(userLat, userLng);
        const labsList = Array.isArray(labsData) ? labsData : [];
        setLabs(labsList);

        // 2. Dynamic Symptoms
        const sympData = await fetchSymptoms();
        if (Array.isArray(sympData)) {
          setSymptoms(sympData);
        }

        // 3. Dynamic Packages directly from API (Location filtered to 20km)
        const pkgRes = await axios.get('https://admin.medrphalabs.com/api/Package?id=0');
        let pkgList = [];
        if (Array.isArray(pkgRes.data)) {
          pkgList = pkgRes.data;
        } else if (Array.isArray(pkgRes.data?.data)) {
          pkgList = pkgRes.data.data;
        }

        const mappedPackages = pkgList
          .filter(p => p.isActive !== false && (p.packageName || p.name))
          .map(pkg => {
            let imgUrl = null;
            if (pkg.image && typeof pkg.image === 'string' && pkg.image.trim()) {
              const clean = pkg.image.trim();
              if (clean.startsWith('http')) {
                imgUrl = clean;
              } else if (clean.startsWith('PackageImage/') || clean.startsWith('LabImage/') || clean.startsWith('TestImage/')) {
                imgUrl = `https://admin.medrphalabs.com/${clean}`;
              } else {
                imgUrl = `https://admin.medrphalabs.com/PackageImage/${clean}`;
              }
            }
            
            const labObj = allLabs.find(l => Number(l.id) === Number(pkg.labId)) || labsList.find(l => Number(l.id) === Number(pkg.labId));
            const labName = labObj?.name || labObj?.labName || labObj?.lab_name || (pkg.labId === 1 ? 'DGchem Lab' : (pkg.labId === 3 ? 'DOCOPD LAB' : 'Certified Partner Lab'));

            return {
              ...pkg,
              dynamicImageUrl: imgUrl,
              assignedLabName: labName,
            };
          });

        let filteredPkgs = mappedPackages.filter(p => {
          if (!userLat || !userLng) return true;
          let pLat = Number(p.latitude || p.Latitude || p.lat || p.Lat);
          let pLng = Number(p.longitude || p.Longitude || p.Longnitude || p.longnitude || p.lng || p.Lng);
          
          if (isNaN(pLat) || isNaN(pLng) || !pLat || !pLng) {
            const matchedLab = allLabs.find(l => Number(l.id) === Number(p.labId)) || labsList.find(l => Number(l.id) === Number(p.labId));
            if (matchedLab) {
              pLat = Number(matchedLab.latitude || matchedLab.Latitude || matchedLab.lat || matchedLab.Lat);
              pLng = Number(matchedLab.longitude || matchedLab.Longitude || matchedLab.Longnitude || matchedLab.longnitude || matchedLab.lng || matchedLab.Lng);
            }
          }

          if (isNaN(pLat) || isNaN(pLng) || !pLat || !pLng) return false;
          return getDistanceKm(Number(userLat), Number(userLng), pLat, pLng) <= 20;
        });
        setPackages(filteredPkgs);

        // 4. Diagnostic Tests - Strictly filtered by 20km
        const testRes = await getAllTests(1, 1000);
        const testData = Array.isArray(testRes?.data) ? testRes.data : (Array.isArray(testRes) ? testRes : []);
        let filteredTests = testData.filter(t => {
          if (!userLat || !userLng) return true;
          let tLat = Number(t.latitude || t.Latitude || t.lat || t.Lat);
          let tLng = Number(t.longitude || t.Longitude || t.Longnitude || t.longnitude || t.lng || t.Lng);
          
          if (isNaN(tLat) || isNaN(tLng) || !tLat || !tLng) {
            const matchedLab = allLabs.find(l => Number(l.id) === Number(t.LabId || t.labId)) || labsList.find(l => Number(l.id) === Number(t.LabId || t.labId));
            if (matchedLab) {
              tLat = Number(matchedLab.latitude || matchedLab.Latitude || matchedLab.lat || matchedLab.Lat);
              tLng = Number(matchedLab.longitude || matchedLab.Longitude || matchedLab.Longnitude || matchedLab.longnitude || matchedLab.lng || matchedLab.Lng);
            }
          }

          if (isNaN(tLat) || isNaN(tLng) || !tLat || !tLng) return false;
          return getDistanceKm(Number(userLat), Number(userLng), tLat, tLng) <= 20;
        });
        setTests(filteredTests);

      } catch (err) {
        console.error('Error fetching lab data:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [coords?.lat, coords?.lng, selectedCity]);

  const isTestInCart = (testId) => {
    return cart.some((item) => Number(item.productId) === Number(testId) && Number(item.categoryId) === 2);
  };

  const isPackageInCart = (packageId) => {
    return cart.some((item) => Number(item.productId) === Number(packageId) && Number(item.categoryId) === 3);
  };

  const handleTestCartToggle = async (test) => {
    const testId = test.testID || test.testId || test.id;
    if (isTestInCart(testId)) {
      removeItem(testId, 2);
    } else {
      await addWithReplaceCheck({
        productId: testId,
        name: test.testName || test.name,
        price: test.sellingPrice || test.testPrice || test.price || 0,
        discountedPrice: test.discount || 0,
        categoryId: 2,
        storeId: test.labId || 1,
        image: test.testImage || '',
      });
    }
  };

  const handlePackageCartToggle = async (pkg) => {
    const pkgId = pkg.packageId || pkg.id;
    if (isPackageInCart(pkgId)) {
      removeItem(pkgId, 3);
    } else {
      await addWithReplaceCheck({
        productId: pkgId,
        name: pkg.packageName || pkg.name,
        price: pkg.packagePrice || pkg.price || 0,
        discountedPrice: pkg.discountPrice || 0,
        categoryId: 3,
        storeId: pkg.labId || 1,
        image: pkg.dynamicImageUrl || pkg.image || '',
      });
    }
  };

  // Filter packages by selected lab and search
  const dynamicPackages = packages.filter((pkg) => {
    const pkgName = (pkg.packageName || pkg.name || '').toLowerCase();
    const matchesSearch = !search || pkgName.includes(search.toLowerCase());
    if (!matchesSearch) return false;

    if (selectedLabId === 'all') return true;
    return Number(pkg.labId) === Number(selectedLabId);
  });

  // Filter tests by selected lab and search
  const dynamicTests = tests.filter((t) => {
    const tName = (t.testName || t.name || '').toLowerCase();
    const matchesSearch = !search || tName.includes(search.toLowerCase());
    if (!matchesSearch) return false;

    if (selectedLabId === 'all') return true;
    return Number(t.labId || t.LabId) === Number(selectedLabId);
  });

  // Filter ONLY Popular Labs
  const popularLabs = labs.filter((lab) => {
    const labName = (lab.name || lab.labName || '').toLowerCase();
    const labAddr = (lab.address || '').toLowerCase();
    const company = (lab.companyName || '').toLowerCase();
    const q = search.toLowerCase();
    const matchesSearch = labName.includes(q) || labAddr.includes(q) || company.includes(q);
    return matchesSearch;
  });

  const totalCartPrice = cart.reduce((sum, item) => sum + (Number(item.price) || 0) * (Number(item.qty) || 1), 0);

  return (
    <div className='min-h-screen bg-[#F8FAFC] pb-32 text-slate-800 font-sans'>
      {LoginPopupComponent}

      {/* ================= 1. PAGE HEADER ================= */}
      <div className='bg-gradient-to-r from-red-600 via-rose-600 to-red-700 text-white py-6 px-4 sm:px-6 lg:px-10 shadow-md'>
        <div className='max-w-[1720px] mx-auto'>
          <div className='flex flex-col md:flex-row md:items-center justify-between gap-4'>
            <div>
              <div className='flex items-center gap-2'>
                <span className='bg-white/20 text-white text-[10px] font-black px-2.5 py-0.5 rounded-full uppercase tracking-wider flex items-center gap-1'>
                  <Flame size={11} className='text-amber-300' />
                  <span>Popular Diagnostic Labs</span>
                </span>
                <span className='text-red-100 text-xs font-semibold'>
                  {popularLabs.length} Top Labs Active
                </span>
              </div>
              <h1 className='text-2xl sm:text-3xl font-black mt-1 tracking-tight'>
                Popular Diagnostic Labs
              </h1>
              <p className='text-xs sm:text-sm text-red-100 mt-1 flex items-center gap-1.5 font-medium'>
                <MapPin size={14} className='text-yellow-300 shrink-0' />
                <span className='truncate max-w-lg'>{address || 'Showing popular certified labs and health packages near you'}</span>
              </p>
            </div>

            {/* Search Bar */}
            <div className='w-full md:w-96 relative'>
              <div className='h-12 bg-white rounded-2xl px-4 flex items-center justify-between shadow-lg text-slate-800 border border-red-100'>
                <Search size={18} className='text-red-500 shrink-0 mr-2.5' />
                <input
                  type='text'
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder='Search popular labs or packages...'
                  className='w-full bg-transparent focus:outline-none text-xs sm:text-sm font-medium text-slate-800 placeholder-slate-400'
                />
                {search ? (
                  <button onClick={() => setSearch('')} className='p-1 text-slate-400 hover:text-slate-600'>
                    <X size={16} />
                  </button>
                ) : (
                  <Mic size={18} className='text-red-500' />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='w-full max-w-[1720px] mx-auto px-4 sm:px-6 lg:px-10 2xl:px-16 mt-6 space-y-8'>
        
        {/* ================= 2. ATTRACTIVE HEALTH CONCERNS & SYMPTOMS SCROLLER ================= */}
        <div className='bg-white border border-slate-200/90 rounded-3xl p-5 shadow-2xs'>
          <div className='flex items-center justify-between mb-4'>
            <div className='flex items-center gap-2.5'>
              <div className='w-1.5 h-5 bg-gradient-to-b from-red-500 to-rose-600 rounded-full' />
              <div>
                <h3 className='text-sm sm:text-base font-extrabold text-slate-900 leading-tight'>
                  Test by Health Concerns & Conditions
                </h3>
                <p className='text-[11px] text-slate-500 font-medium'>Select a condition to see recommended diagnostic tests</p>
              </div>
            </div>
            <button 
              onClick={() => navigate('/all-tests')}
              className='text-xs font-bold text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-xl transition-all flex items-center gap-1'
            >
              <span>View All Concerns</span>
              <ChevronRight size={13} />
            </button>
          </div>

          <div className='flex items-center gap-4 sm:gap-5 overflow-x-auto no-scrollbar py-2'>
            {(symptoms.length > 0 ? symptoms : [
              { name: 'Diabetes', id: 1 },
              { name: 'Heart Care', id: 2 },
              { name: 'Thyroid', id: 3 },
              { name: 'Fever & Infection', id: 4 },
              { name: 'Liver Check', id: 5 },
              { name: 'Kidney Health', id: 6 },
              { name: 'Vitamins', id: 7 },
              { name: 'Full Body', id: 8 }
            ]).map((symp, idx) => {
              const sympName = symp.name || symp.symptomName || 'Condition';
              const imgName = symp.symptoms_Image || symp.symptomsImage || symp.image;
              const imgUrl = imgName ? (imgName.startsWith('http') ? imgName : `https://admin.medrphalabs.com/SymptomsImage/${imgName}`) : null;

              return (
                <div 
                  key={symp.id || idx}
                  onClick={() => navigate('/SymptomsDetails', { state: { symptom: symp } })}
                  className='flex flex-col items-center flex-shrink-0 cursor-pointer group text-center'
                >
                  {/* Outer gradient glow ring */}
                  <div className='w-16 h-16 sm:w-20 sm:h-20 rounded-full p-1 bg-gradient-to-tr from-red-500 via-rose-400 to-amber-300 shadow-md transition-all'>
                    <div className='w-full h-full rounded-full bg-white flex items-center justify-center p-3 overflow-hidden shadow-inner'>
                      {imgUrl ? (
                        <img 
                          src={imgUrl} 
                          alt={sympName} 
                          className='w-full h-full object-contain'
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <HeartPulse size={26} className='text-red-600' />
                      )}
                    </div>
                  </div>

                  <span className='text-xs font-bold text-slate-800 group-hover:text-red-600 mt-2 max-w-[80px] truncate leading-tight transition-colors'>
                    {sympName}
                  </span>
                  <span className='text-[10px] text-slate-400 font-medium mt-0.5'>
                    Explore Tests
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* ================= 3. POPULAR DIAGNOSTIC LABS (CLEAN FIXED LOGO - NO HOVER ZOOM) ================= */}
        <div className='bg-white border border-slate-200/90 rounded-3xl p-5 shadow-2xs'>
          <div className='flex items-center justify-between mb-4'>
            <div className='flex items-center gap-2.5'>
              <div className='w-1.5 h-5 bg-gradient-to-b from-red-600 to-rose-600 rounded-full' />
              <div>
                <h3 className='text-sm sm:text-base font-extrabold text-slate-900 leading-tight flex items-center gap-1.5'>
                  <span>Popular Diagnostic Labs</span>
                  <span className='text-[10px] bg-red-100 text-red-700 font-bold px-2 py-0.5 rounded-full uppercase'>
                    Top Rated
                  </span>
                </h3>
                <p className='text-[11px] text-slate-500 font-medium'>Showing exclusively popular NABL & ICMR accredited testing centers</p>
              </div>
            </div>
          </div>

          {loading ? (
            <div className='grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4'>
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className='bg-white rounded-2xl p-3.5 border border-slate-200 animate-pulse space-y-3'>
                  <div className='h-36 bg-slate-200 rounded-xl w-full' />
                  <div className='h-4 bg-slate-200 rounded w-3/4' />
                  <div className='h-3 bg-slate-100 rounded w-1/2' />
                  <div className='h-8 bg-slate-100 rounded-lg w-full' />
                </div>
              ))}
            </div>
          ) : popularLabs.length === 0 ? (
            <div className='rounded-3xl p-12 text-center max-w-md mx-auto my-4'>
              <FlaskConical size={44} className='text-slate-300 mx-auto mb-3' />
              <h3 className='text-base font-bold text-slate-800'>No Popular Labs Found</h3>
              <p className='text-xs text-slate-500 mt-1'>Try another search term or refresh.</p>
              <button
                onClick={() => setSearch('')}
                className='mt-4 bg-red-600 text-white font-bold text-xs px-5 py-2.5 rounded-xl hover:bg-red-700 shadow-xs'
              >
                Clear Search
              </button>
            </div>
          ) : (
            <div className='grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4'>
              {popularLabs.map((lab, idx) => (
                <LabCard key={lab.id || lab.labId || idx} lab={lab} />
              ))}
            </div>
          )}
        </div>

        {/* ================= 4. POPULAR HEALTH PACKAGES SLIDER (UNCUT CLEAN IMAGES) ================= */}
        <div className='bg-white border border-slate-200/90 rounded-3xl p-5 shadow-2xs relative'>
          
          {/* Header with Navigation Controls */}
          <div className='flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4'>
            <div className='flex items-center gap-2.5'>
              <div className='w-1.5 h-5 bg-gradient-to-b from-blue-600 to-indigo-600 rounded-full' />
              <div>
                <h3 className='text-sm sm:text-base font-extrabold text-slate-900 leading-tight'>
                  Popular Health Checkup Packages
                </h3>
                <p className='text-[11px] text-slate-500 font-medium'>Showing {dynamicPackages.length} packages categorized by popular labs</p>
              </div>
            </div>

            <div className='flex items-center gap-2 self-end sm:self-auto'>
              {/* Slider Arrow Buttons */}
              <button
                onClick={() => slidePackages('left')}
                className='w-8 h-8 rounded-full bg-slate-100 hover:bg-red-600 hover:text-white text-slate-700 flex items-center justify-center transition-all shadow-2xs'
                title='Slide Left'
              >
                <ChevronLeft size={16} />
              </button>
              <button
                onClick={() => slidePackages('right')}
                className='w-8 h-8 rounded-full bg-slate-100 hover:bg-red-600 hover:text-white text-slate-700 flex items-center justify-center transition-all shadow-2xs'
                title='Slide Right'
              >
                <ChevronRight size={16} />
              </button>

              <button 
                onClick={() => navigate('/all-packages')}
                className='text-xs font-bold text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-xl transition-all flex items-center gap-1 ml-1'
              >
                <span>View All</span>
                <ChevronRight size={13} />
              </button>
            </div>
          </div>

          {/* Dynamic Lab Filter Chips for Packages */}
          <div className='flex items-center gap-2 overflow-x-auto no-scrollbar pb-3 mb-2'>
            <button
              onClick={() => setSelectedLabId('all')}
              className={`text-xs font-bold px-3.5 py-1.5 rounded-xl transition-all whitespace-nowrap border flex items-center gap-1.5 ${
                selectedLabId === 'all'
                  ? 'bg-red-600 text-white border-red-600 shadow-xs'
                  : 'bg-slate-50 text-slate-700 border-slate-200 hover:border-red-300'
              }`}
            >
              <span>All Popular Labs ({packages.length})</span>
            </button>

            {popularLabs.map((lab) => {
              const labPkgCount = packages.filter(p => Number(p.labId) === Number(lab.id)).length;
              return (
                <button
                  key={lab.id}
                  onClick={() => setSelectedLabId(lab.id)}
                  className={`text-xs font-bold px-3.5 py-1.5 rounded-xl transition-all whitespace-nowrap border flex items-center gap-1.5 ${
                    Number(selectedLabId) === Number(lab.id)
                      ? 'bg-red-600 text-white border-red-600 shadow-xs'
                      : 'bg-slate-50 text-slate-700 border-slate-200 hover:border-red-300'
                  }`}
                >
                  <Building2 size={12} className={Number(selectedLabId) === Number(lab.id) ? 'text-white' : 'text-slate-400'} />
                  <span>{lab.name || lab.labName} ({labPkgCount})</span>
                </button>
              );
            })}
          </div>

          {/* Dynamic Package Cards SLIDER with UNCUT IMAGES */}
          {dynamicPackages.length === 0 ? (
            <div className='bg-slate-50 rounded-2xl p-8 text-center border border-slate-200 my-2'>
              <Package size={36} className='text-slate-300 mx-auto mb-2' />
              <p className='text-xs font-bold text-slate-700'>No health packages found for selected lab filter.</p>
              <button
                onClick={() => setSelectedLabId('all')}
                className='mt-3 text-xs font-bold text-red-600 bg-white border border-red-200 px-3 py-1.5 rounded-lg'
              >
                View All Labs Packages
              </button>
            </div>
          ) : (
            <div 
              ref={packageSliderRef}
              className='flex items-stretch gap-4 overflow-x-auto no-scrollbar pb-3 pt-1 scroll-smooth'
            >
              {dynamicPackages.map((pkg, idx) => (
                <PackageCard
                  key={pkg.packageId || pkg.id || idx}
                  pkg={pkg}
                  inCart={isPackageInCart(pkg.packageId || pkg.id)}
                  onCartToggle={handlePackageCartToggle}
                  className="w-[260px] sm:w-[280px] flex-shrink-0"
                />
              ))}
            </div>
          )}
        </div>

        {/* ================= 5. POPULAR DIAGNOSTIC TESTS SLIDER (Location & Lab-Wise) ================= */}
        <div className='bg-white border border-slate-200/90 rounded-3xl p-5 shadow-2xs relative'>
          
          {/* Header with Navigation Controls */}
          <div className='flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4'>
            <div className='flex items-center gap-2.5'>
              <div className='w-1.5 h-5 bg-gradient-to-b from-red-600 to-rose-600 rounded-full' />
              <div>
                <h3 className='text-sm sm:text-base font-extrabold text-slate-900 leading-tight'>
                  Diagnostic Blood & Lab Tests
                </h3>
                <p className='text-[11px] text-slate-500 font-medium'>Showing {dynamicTests.length} tests available at nearby certified labs</p>
              </div>
            </div>

            <div className='flex items-center gap-2 self-end sm:self-auto'>
              {/* Slider Arrow Buttons */}
              <button
                onClick={() => slideTests('left')}
                className='w-8 h-8 rounded-full bg-slate-100 hover:bg-red-600 hover:text-white text-slate-700 flex items-center justify-center transition-all shadow-2xs'
                title='Slide Left'
              >
                <ChevronLeft size={16} />
              </button>
              <button
                onClick={() => slideTests('right')}
                className='w-8 h-8 rounded-full bg-slate-100 hover:bg-red-600 hover:text-white text-slate-700 flex items-center justify-center transition-all shadow-2xs'
                title='Slide Right'
              >
                <ChevronRight size={16} />
              </button>

              <button 
                onClick={() => navigate('/all-tests')}
                className='text-xs font-bold text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-xl transition-all flex items-center gap-1 ml-1'
              >
                <span>View All Tests</span>
                <ChevronRight size={13} />
              </button>
            </div>
          </div>

          {/* Dynamic Test Cards SLIDER */}
          {dynamicTests.length === 0 ? (
            <div className='bg-slate-50 rounded-2xl p-8 text-center border border-slate-200 my-2'>
              <FlaskConical size={36} className='text-slate-300 mx-auto mb-2' />
              <p className='text-xs font-bold text-slate-700'>No diagnostic tests found for selected lab filter.</p>
              <button
                onClick={() => setSelectedLabId('all')}
                className='mt-3 text-xs font-bold text-red-600 bg-white border border-red-200 px-3 py-1.5 rounded-lg'
              >
                View All Tests
              </button>
            </div>
          ) : (
            <div 
              ref={testSliderRef}
              className='flex items-stretch gap-4 overflow-x-auto no-scrollbar pb-3 pt-1 scroll-smooth'
            >
              {dynamicTests.map((test, idx) => (
                <TestCard
                  key={test.testID || test.testId || idx}
                  test={test}
                  inCart={isTestInCart(test.testID || test.testId)}
                  onCartToggle={handleTestCartToggle}
                  className="w-[240px] sm:w-[260px] flex-shrink-0"
                />
              ))}
            </div>
          )}
        </div>

      </div>

      {/* ================= 5. STICKY BOTTOM CART BAR ================= */}
      {cart.length > 0 && (
        <div className='fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-md border-t border-slate-200 p-3 sm:p-4 shadow-2xl'>
          <div className='max-w-[1720px] mx-auto px-4 sm:px-6 lg:px-10 flex items-center justify-between'>
            <div className='flex items-center gap-3'>
              <div className='w-10 h-10 rounded-xl bg-red-50 text-red-600 flex items-center justify-center font-black'>
                <ShoppingBag size={20} />
              </div>
              <div>
                <span className='text-xs font-semibold text-slate-500'>
                  {cart.length} {cart.length === 1 ? 'Item' : 'Items'} in Cart
                </span>
                <p className='text-base sm:text-lg font-black text-slate-900'>
                  Total: ₹{totalCartPrice}
                </p>
              </div>
            </div>

            <button
              onClick={() => navigate('/cart')}
              className='bg-red-600 hover:bg-red-700 text-white font-black text-xs sm:text-sm px-6 py-2.5 rounded-xl shadow-md transition-all flex items-center gap-1.5'
            >
              <span>View Cart & Checkout</span>
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

    </div>
  );
}
